import { useCallback, useEffect, useRef, useState } from "react";
import type { ChangeEvent, FormEvent } from "react";
import { useSearchParams } from "react-router-dom";
import { approveDeviceCode, denyDeviceCode, getDeviceCode } from "@/api/endpoints";
import { ApiError, ApiUnavailableError } from "@/api/client";
import {
  formatExpiresIn,
  formatRequestedAgo,
  isCompleteDeviceCode,
  isDeviceCodeExpired,
  normalizeDeviceCodeInput,
} from "@/lib/deviceCode";
import type { DeviceCodeAuthorization } from "@/types/api";

type ActionKind = "approve" | "deny";

type LookupState =
  | { kind: "idle" }
  | { kind: "loading" }
  | { kind: "loaded"; device: DeviceCodeAuthorization }
  | { kind: "error"; message: string };

type ActionState =
  | { kind: "idle" }
  | { kind: "confirming"; action: ActionKind }
  | { kind: "submitting"; action: ActionKind }
  | { kind: "done"; action: ActionKind }
  | { kind: "error"; action: ActionKind; message: string };

function describeError(error: unknown): string {
  if (error instanceof ApiUnavailableError) {
    return error.message;
  }
  if (error instanceof ApiError) {
    if (error.status === 404) {
      return "That code isn't valid, or it's already been used.";
    }
    return error.problem.detail || error.problem.title;
  }
  return "Something went wrong. Please try again.";
}

export function DeviceLinkPage() {
  const [searchParams] = useSearchParams();
  const codeFromUrl = searchParams.get("code") ?? "";

  const [codeInput, setCodeInput] = useState(() => normalizeDeviceCodeInput(codeFromUrl));
  const [lookup, setLookup] = useState<LookupState>({ kind: "idle" });
  const [action, setAction] = useState<ActionState>({ kind: "idle" });
  const [now, setNow] = useState(() => new Date());
  const lookedUpCode = useRef<string | null>(null);

  useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  const runLookup = useCallback(async (userCode: string) => {
    lookedUpCode.current = userCode;
    setLookup({ kind: "loading" });
    setAction({ kind: "idle" });
    try {
      const response = await getDeviceCode(userCode);
      setLookup({ kind: "loaded", device: response.data });
    } catch (error) {
      setLookup({ kind: "error", message: describeError(error) });
    }
  }, []);

  useEffect(() => {
    if (isCompleteDeviceCode(codeInput) && lookedUpCode.current !== codeInput) {
      void runLookup(codeInput);
    }
  }, [codeInput, runLookup]);

  function handleInputChange(event: ChangeEvent<HTMLInputElement>) {
    const normalized = normalizeDeviceCodeInput(event.target.value);
    setCodeInput(normalized);
    if (!isCompleteDeviceCode(normalized)) {
      lookedUpCode.current = null;
      setLookup({ kind: "idle" });
      setAction({ kind: "idle" });
    }
  }

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (isCompleteDeviceCode(codeInput)) {
      lookedUpCode.current = null;
      void runLookup(codeInput);
    }
  }

  function handleTryAnotherCode() {
    lookedUpCode.current = null;
    setCodeInput("");
    setLookup({ kind: "idle" });
    setAction({ kind: "idle" });
  }

  async function performAction(kind: ActionKind) {
    setAction({ kind: "submitting", action: kind });
    try {
      const response = kind === "approve"
        ? await approveDeviceCode(codeInput)
        : await denyDeviceCode(codeInput);
      setLookup({ kind: "loaded", device: response.data });
      setAction({ kind: "done", action: kind });
    } catch (error) {
      setAction({ kind: "error", action: kind, message: describeError(error) });
    }
  }

  const showCodeForm = !isCompleteDeviceCode(codeInput) || lookup.kind === "error";

  return (
    <div className="device-link-page">
      <div className="login-card device-link-card">
        <h1 className="device-link-title">Sign in a device</h1>

        {showCodeForm && (
          <form onSubmit={handleSubmit} className="device-link-code-form">
            <p className="device-link-subtitle">
              Enter the code shown on the device you&rsquo;re signing in.
            </p>
            {lookup.kind === "error" && (
              <p className="settings-status settings-status--error">{lookup.message}</p>
            )}
            <label>
              Device code
              <input
                type="text"
                inputMode="text"
                autoCapitalize="characters"
                autoComplete="off"
                autoCorrect="off"
                spellCheck={false}
                placeholder="XXXX-XXXX"
                maxLength={9}
                value={codeInput}
                onChange={handleInputChange}
                aria-label="Device code"
              />
            </label>
            <button type="submit" disabled={!isCompleteDeviceCode(codeInput)}>
              Continue
            </button>
          </form>
        )}

        {lookup.kind === "loading" && (
          <p className="device-link-subtitle" role="status">Looking up code&hellip;</p>
        )}

        {lookup.kind === "loaded" && (
          <DeviceLinkDetail
            device={lookup.device}
            action={action}
            now={now}
            onRequestConfirm={(kind) => setAction({ kind: "confirming", action: kind })}
            onCancelConfirm={() => setAction({ kind: "idle" })}
            onConfirm={performAction}
            onTryAnotherCode={handleTryAnotherCode}
          />
        )}
      </div>
    </div>
  );
}

function DeviceLinkDetail({
  device,
  action,
  now,
  onRequestConfirm,
  onCancelConfirm,
  onConfirm,
  onTryAnotherCode,
}: {
  device: DeviceCodeAuthorization;
  action: ActionState;
  now: Date;
  onRequestConfirm: (kind: ActionKind) => void;
  onCancelConfirm: () => void;
  onConfirm: (kind: ActionKind) => void;
  onTryAnotherCode: () => void;
}) {
  const { device_name, client_name, requested_at, expires_at, status } = device.attributes;
  const expiredLocally = status === "pending" && isDeviceCodeExpired(expires_at, now);
  const effectiveStatus = expiredLocally ? "expired" : status;

  if (action.kind === "done") {
    return (
      <div>
        <p className="settings-status settings-status--success">
          {action.action === "approve"
            ? `Signed in on ${device_name}. You can close this page.`
            : "Request denied. You can close this page."}
        </p>
      </div>
    );
  }

  return (
    <div className="device-link-detail">
      <dl className="device-link-facts">
        <div>
          <dt>Device</dt>
          <dd>{device_name || "Unknown device"}</dd>
        </div>
        <div>
          <dt>App</dt>
          <dd>{client_name || "Unknown app"}</dd>
        </div>
        <div>
          <dt>Requested</dt>
          <dd>{formatRequestedAgo(requested_at, now) || "recently"}</dd>
        </div>
        <div>
          <dt>Expires</dt>
          <dd>{formatExpiresIn(expires_at, now) || "soon"}</dd>
        </div>
      </dl>

      {effectiveStatus === "expired" && (
        <p className="settings-status settings-status--warning">
          This code has expired. Go back to the device and request a new one.
        </p>
      )}
      {effectiveStatus === "approved" && (
        <p className="settings-status settings-status--success">
          This device was already signed in.
        </p>
      )}
      {effectiveStatus === "denied" && (
        <p className="settings-status settings-status--error">
          This request was already denied.
        </p>
      )}

      {action.kind === "error" && (
        <p className="settings-status settings-status--error">{action.message}</p>
      )}

      {effectiveStatus === "pending" && action.kind === "confirming" && (
        <div className="device-link-confirm">
          <p className="device-link-subtitle">
            {action.action === "approve"
              ? `Approve sign-in on ${device_name || "this device"}?`
              : `Deny sign-in on ${device_name || "this device"}?`}
          </p>
          <div className="device-link-actions">
            <button
              type="button"
              className="device-link-btn device-link-btn--cancel"
              onClick={onCancelConfirm}
            >
              Cancel
            </button>
            <button
              type="button"
              className={
                action.action === "approve"
                  ? "device-link-btn device-link-btn--approve"
                  : "device-link-btn device-link-btn--deny"
              }
              onClick={() => onConfirm(action.action)}
            >
              {action.action === "approve" ? "Yes, approve" : "Yes, deny"}
            </button>
          </div>
        </div>
      )}

      {effectiveStatus === "pending" && action.kind !== "confirming" && (
        <div className="device-link-actions">
          <button
            type="button"
            className="device-link-btn device-link-btn--deny"
            disabled={action.kind === "submitting"}
            onClick={() => onRequestConfirm("deny")}
          >
            {action.kind === "submitting" && action.action === "deny" ? "Denying..." : "Deny"}
          </button>
          <button
            type="button"
            className="device-link-btn device-link-btn--approve"
            disabled={action.kind === "submitting"}
            onClick={() => onRequestConfirm("approve")}
          >
            {action.kind === "submitting" && action.action === "approve" ? "Approving..." : "Approve"}
          </button>
        </div>
      )}

      {effectiveStatus !== "pending" && (
        <button type="button" className="device-link-btn device-link-btn--cancel" onClick={onTryAnotherCode}>
          Enter a different code
        </button>
      )}
    </div>
  );
}
